import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import NoIndex from "@/components/NoIndex";
import SlideEditor from "@/components/SlideEditor";
import { useAuth } from "@/lib/auth";
import LabStamp from "@/components/LabStamp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * Live-events admin console. JWT-gated to a hardcoded allowlist on the
 * backend (see server.py::LIVE_ADMIN_EMAILS). Create events, toggle
 * english/hebrew mode, deactivate when a show ends.
 */
export default function LiveAdmin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [mode, setMode] = useState("english");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);
  const [clearing, setClearing] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/app/login", { state: { from: "/live-admin" } });
      return;
    }
    refresh();
  }, [authLoading, user]);

  const refresh = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await ax.get("/admin/live/events");
      setEvents(data);
    } catch (e) {
      if (e.response?.status === 403) {
        setErr("This account isn't on the live-admin allowlist.");
      } else {
        setErr(e.response?.data?.detail || "Couldn't load events.");
      }
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are both required.");
      return;
    }
    setCreating(true);
    try {
      await ax.post("/admin/live/events", {
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        mode,
      });
      toast.success(`Event "${name}" created.`);
      setName("");
      setSlug("");
      setMode("english");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create event.");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (ev) => {
    try {
      await ax.patch(`/admin/live/events/${ev.slug}`, { is_active: !ev.is_active });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  const changeMode = async (ev, newMode) => {
    try {
      await ax.patch(`/admin/live/events/${ev.slug}`, { mode: newMode });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  const clearAudienceData = async (ev) => {
    if (!window.confirm(
      `Clear rehearsal data for "${ev.name}"?\n\nRemoves: everyone who joined, every word submitted, every word pushed to the screen, recap spotlights, the volunteer's notes, the wheel pick and the Hebrew staff demo.\n\nKeeps: all slides, speaker notes, wheel songs, location and parsha.`
    )) return;
    setClearing(ev.slug);
    try {
      const { data } = await ax.post(`/admin/live/events/${ev.slug}/audience/reset`);
      const d = data.deleted || {};
      toast.success(`Cleared ${d.participants || 0} people, ${d.submissions || 0} words, ${d.selections || 0} pushes.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear the data.");
    } finally {
      setClearing("");
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"? This removes all submissions and selections.`)) return;
    try {
      await ax.delete(`/admin/live/events/${ev.slug}`);
      toast.success("Event deleted.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  const slugify = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  if (user && !user.is_admin) {
    return (
      <div
        className="min-h-screen w-full"
        style={{ backgroundColor: "#FAF8F5", color: "#1A1523", fontFamily: "'Courier Prime', monospace" }}
       
      >
        <NoIndex title="Compose Yourself! by Dan Berry" />
        <div className="max-w-[1000px] mx-auto px-6 py-24" data-testid="live-admin-denied">
          <h1 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Not your panel.
          </h1>
          <p className="mt-4 text-sm" style={{ color: "#5C5268" }}>
            {user.email} isn&apos;t on the admin list.
          </p>
          <Link to="/" className="mt-8 inline-block text-xs underline underline-offset-4" style={{ color: "#E86F4A" }}>
            Back to the site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: "#FAF8F5", color: "#1A1523", fontFamily: "'Courier Prime', monospace" }} data-live-dark="">
      <NoIndex title="Compose Yourself! by Dan Berry" />
      <Toaster position="bottom-right" richColors />
      <header className="max-w-[1000px] mx-auto px-6 pt-10 pb-6 flex items-baseline justify-between">
        <div>
          <LabStamp testId="live-admin-lab-stamp" />
          <p className="mt-2 text-[10px] uppercase tracking-[0.32em]" style={{ color: "#8A6514" }}>Live admin</p>
          <h1 className="text-3xl sm:text-4xl mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="live-admin-title">
            Live events
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setTemplateOpen(true)}
            data-testid="live-admin-open-template"
            className="h-9 rounded-full text-xs border-[#D6CBB8]"
          >
            Master template
          </Button>
          <Link to="/app" className="text-xs underline underline-offset-4 opacity-70 hover:opacity-100">← app</Link>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 pb-20 space-y-8">
        {err && (
          <div className="rounded-md p-4 border" style={{ borderColor: "rgba(255,120,120,0.4)", backgroundColor: "rgba(255,50,50,0.08)", color: "#E86F4A" }} data-testid="live-admin-error">
            {err}
          </div>
        )}

        <section className="rounded-2xl border p-6 backdrop-blur-md" style={{ borderColor: "rgba(217,155,38,0.35)", backgroundColor: "rgba(255,255,255,0.86)" }}>
          <h2 className="text-xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>New event</h2>
          <form onSubmit={createEvent} className="grid sm:grid-cols-[1.4fr_1fr_0.8fr_auto] gap-3 items-end" data-testid="live-admin-create-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70">Event name</label>
              <Input value={name} onChange={(e) => { setName(e.target.value); if (!slug) setSlug(slugify(e.target.value)); }} placeholder="Boca House Concert" className="h-11 bg-white border-[#D6CBB8]" data-testid="live-admin-name" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70">Slug (URL)</label>
              <Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} placeholder="boca" className="h-11 bg-white border-[#D6CBB8]" data-testid="live-admin-slug" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70">Mode</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-11 bg-white border-[#D6CBB8]" data-testid="live-admin-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="english">English</SelectItem><SelectItem value="hebrew">Hebrew</SelectItem></SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={creating} data-testid="live-admin-create" className="h-11 rounded-full" style={{ backgroundColor: "#D99B26", color: "#FFFFFF", border: "none", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", fontSize: "12px" }}>
              {creating ? "…" : "Create"}
            </Button>
          </form>
        </section>

        <section>
          <h2 className="text-xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>Existing events</h2>
          {loading ? (
            <p className="text-sm opacity-60">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-sm opacity-60" data-testid="live-admin-empty">No events yet. Create one above.</p>
          ) : (
            <ul className="space-y-3" data-testid="live-admin-events">
              {events.map((ev) => (
                <li key={ev.slug} className="rounded-2xl border p-5 backdrop-blur-md" style={{ borderColor: "rgba(26,21,35,0.08)", backgroundColor: "rgba(255,255,255,0.8)" }} data-testid={`live-admin-event-${ev.slug}`}>
                  <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div>
                      <p className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{ev.name}</p>
                      <p className="text-xs opacity-60 mt-0.5">/live/{ev.slug} · mode: {ev.mode} · {ev.is_active ? <span style={{ color: "#15803D" }}>active</span> : <span style={{ color: "#E86F4A" }}>paused</span>}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/live/${ev.slug}`} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 opacity-70 hover:opacity-100" data-testid={`live-admin-open-audience-${ev.slug}`}>audience →</Link>
                      <Link to={`/live/${ev.slug}/admin`} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 opacity-70 hover:opacity-100" data-testid={`live-admin-open-moderator-${ev.slug}`}>moderator →</Link>
                      <Link to={`/live/${ev.slug}/display`} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 opacity-70 hover:opacity-100" data-testid={`live-admin-open-display-${ev.slug}`}>display →</Link>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 items-center">
                    <Select value={ev.mode} onValueChange={(v) => changeMode(ev, v)}>
                      <SelectTrigger className="h-9 bg-white border-[#D6CBB8] w-32 text-xs" data-testid={`live-admin-mode-${ev.slug}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="english">English</SelectItem><SelectItem value="hebrew">Hebrew</SelectItem></SelectContent>
                    </Select>
                    <Button type="button" variant="outline" onClick={() => toggleActive(ev)} className="h-9 rounded-full border-[#D6CBB8] text-xs" data-testid={`live-admin-toggle-${ev.slug}`}>
                      {ev.is_active ? "Pause" : "Resume"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => clearAudienceData(ev)}
                      disabled={clearing === ev.slug}
                      className="h-9 rounded-full border-[#D6CBB8] text-xs"
                      style={{ color: "#2E8C9E" }}
                      title="Wipes joins, submissions, pushed words and recap picks. Keeps every slide and speaker note."
                      data-testid={`live-admin-clear-audience-${ev.slug}`}
                    >
                      {clearing === ev.slug ? "Clearing…" : "Clear rehearsal data"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => deleteEvent(ev)} className="h-9 rounded-full border-[#D6CBB8] text-xs ml-auto" style={{ color: "#E86F4A" }} data-testid={`live-admin-delete-${ev.slug}`}>
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {templateOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
          style={{ backgroundColor: "rgba(250,248,245,0.92)", backdropFilter: "blur(6px)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setTemplateOpen(false);
          }}
          data-testid="live-admin-template-modal"
        >
          <div className="w-full pt-10 pb-20" onClick={(e) => e.stopPropagation()}>
            <SlideEditor scope="template" onClose={() => setTemplateOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
